// apps/prompts/src/components/TagTypeahead.tsx
// Input typeahead pour le filtre Tag : recherche les occurrences correspondant
// à la saisie (substring, insensible à la casse) parmi les tags les plus utilisés.
// Composant maison — réutilise les primitives de @neuroforge/ui.
import { cn, AppIcon, IconTag, IconSearch, IconX, useClickOutside } from '@neuroforge/ui'
import { useEffect, useId, useMemo, useRef, useState } from 'react'

export interface TagSuggestion {
  tag: string
  count: number
}

export interface TagTypeaheadProps {
  /** Pool de suggestions (déjà trié par fréquence décroissante) */
  suggestions: TagSuggestion[]
  /** Tag actif (filtre courant) — '' si aucun */
  value: string
  onChange: (tag: string) => void
  /** Nombre max de suggestions affichées */
  max?: number
  className?: string
}

/**
 * Champ de recherche de tag avec autocomplétion. La frappe filtre les tags
 * correspondants ; sélectionner une suggestion applique le filtre. Navigation
 * clavier ↑/↓/Entrée, Échap pour fermer, croix pour réinitialiser.
 */
export default function TagTypeahead({
  suggestions,
  value,
  onChange,
  max = 8,
  className,
}: TagTypeaheadProps) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const ref = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listboxId = useId()
  useClickOutside(ref, () => setOpen(false))

  // Filtre substring insensible à la casse ; on garde l'ordre par fréquence.
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase()
    const pool = q ? suggestions.filter((s) => s.tag.toLowerCase().includes(q)) : suggestions
    return pool.slice(0, max)
  }, [query, suggestions, max])

  useEffect(() => {
    setActiveIndex(0)
  }, [query, open])

  const choose = (tag: string) => {
    onChange(tag)
    setQuery('')
    setOpen(false)
    inputRef.current?.blur()
  }

  const clear = () => {
    onChange('')
    setQuery('')
    setOpen(false)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false)
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setOpen(true)
      setActiveIndex((i) => Math.min(i + 1, matches.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const pick = matches[activeIndex]
      if (pick) choose(pick.tag)
    }
  }

  return (
    <div ref={ref} className={cn('relative', className)} onKeyDown={onKeyDown}>
      {/* Tag actif (chip) — affiché tant qu'un filtre est posé */}
      {value && (
        <button
          type="button"
          onClick={clear}
          className="text-(--accent) bg-(--accent-subtle) mb-2 flex w-full items-center gap-2 rounded-2xl border border-[var(--accent)] px-3 py-2 text-sm font-bold"
        >
          <AppIcon icon={IconTag} size={16} aria-hidden />
          <span className="truncate">#{value}</span>
          <AppIcon icon={IconX} size={16} className="ml-auto shrink-0" aria-hidden />
        </button>
      )}

      <div
        className={cn(
          'flex items-center gap-2 rounded-2xl px-3 py-2.5 transition-all duration-150',
          'border-(--border) border bg-[var(--bg-soft)]',
          open ? 'border-[var(--accent)]' : 'hover:border-[var(--accent)]',
        )}
      >
        <AppIcon icon={IconSearch} size={16} className="text-(--muted) shrink-0" aria-hidden />
        <input
          ref={inputRef}
          type="text"
          value={query}
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          placeholder="Rechercher un tag…"
          onChange={(e) => {
            setQuery(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          className="text-(--text) placeholder:text-(--muted) min-w-0 flex-1 bg-transparent text-sm font-semibold outline-none"
        />
        {query && (
          <button
            type="button"
            onClick={() => {
              setQuery('')
              inputRef.current?.focus()
            }}
            aria-label="Effacer la recherche"
            className="text-(--muted) hover:text-(--text) shrink-0"
          >
            <AppIcon icon={IconX} size={16} aria-hidden />
          </button>
        )}
      </div>

      {open && matches.length > 0 && (
        <ul
          id={listboxId}
          role="listbox"
          aria-label="Tags correspondants"
          className={cn(
            'absolute left-0 right-0 top-full z-50 mt-2 max-h-72 overflow-y-auto rounded-2xl p-1.5',
            'border-(--border) border bg-white shadow-xl dark:bg-slate-900',
          )}
        >
          {matches.map((s, i) => {
            const isActive = i === activeIndex
            const isSelected = s.tag === value
            return (
              <li key={s.tag}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => choose(s.tag)}
                  onMouseEnter={() => setActiveIndex(i)}
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-sm font-semibold transition-colors',
                    isSelected
                      ? 'text-(--accent) bg-(--accent-subtle)'
                      : isActive
                        ? 'text-(--text) bg-(--bg-elevated)'
                        : 'text-(--text-secondary)',
                  )}
                >
                  <AppIcon icon={IconTag} size={16} className="shrink-0 opacity-70" aria-hidden />
                  <span className="truncate">#{s.tag}</span>
                  <span className="text-(--muted) ml-auto shrink-0 text-xs tabular-nums">
                    {s.count}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      )}

      {open && query.trim() && matches.length === 0 && (
        <div
          className={cn(
            'absolute left-0 right-0 top-full z-50 mt-2 rounded-2xl p-3 text-center',
            'border-(--border) border bg-white shadow-xl dark:bg-slate-900',
          )}
        >
          <span className="text-(--muted) text-xs">Aucun tag correspondant</span>
        </div>
      )}
    </div>
  )
}
